/**
 * CommentsSection — the full comments area at the bottom of an estimate.
 *
 * The server decides what a viewer may see (comments.php):
 *   - the OWNER (original link) sees the general thread — visible to anyone with
 *     that link, as it always was — plus one private conversation per share;
 *   - a RECIPIENT (child link) sees only their private conversation with the owner;
 *   - a PROMOTED installer also sees the retailer conversations they were granted.
 * This component groups what it was given by thread and posts into the right one.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Plus, Loader2, Store, HardHat, Globe, Lock, Reply } from 'lucide-react';
import type { Comment } from '../../types';
import type { CommentsViewer, ShareThread } from '../../types/share';
import { fetchComments } from '../../services/api';
import { CommentThread } from './CommentThread';
import { CommentModal } from './CommentModal';
import './CommentsSection.css';

interface CommentsSectionProps {
  estimateGuid: string;
  /** Called after a comment is posted — the owner's shares panel refreshes its counts. */
  onActivity?: () => void;
}

interface Group {
  key: string;
  threadId: number | null;
  title: string;
  kind: 'general' | 'retailer' | 'installer';
  hint?: string;
  readOnly: boolean;
  promoted?: boolean;
  comments: Comment[];
}

type ViewerWithLabels = CommentsViewer & { threadLabels?: Record<string, { label: string; kind: string }> };

export const CommentsSection: React.FC<CommentsSectionProps> = ({ estimateGuid, onActivity }) => {
  const { t } = useTranslation('results');
  const [comments, setComments] = useState<Comment[]>([]);
  const [viewer, setViewer] = useState<ViewerWithLabels | null>(null);
  const [threads, setThreads] = useState<ShareThread[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalThread, setModalThread] = useState<number | null | undefined>(undefined);
  const [replyTo, setReplyTo] = useState<{ parentId: number; author: string } | null>(null);

  const sectionRef = useRef<HTMLDivElement>(null);

  const loadComments = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchComments(estimateGuid);
      setComments(data.comments);
      setViewer(data.viewer as ViewerWithLabels);
      setThreads(data.threads ?? null);
    } catch {
      // The thrown message is an English server/network string; the user gets
      // our translated one and the retry button, which is what they can act on.
      setError(t('comments.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [estimateGuid, t]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  // Came back to the tab to check for replies? Refresh — no polling.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadComments(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadComments]);

  const role = viewer?.role ?? 'none';
  const isOwner = role === 'owner' || role === 'admin';
  const isRecipient = role === 'recipient' || role === 'promoted';
  const grouped = isOwner ? (threads !== null && threads.length > 0) : isRecipient;

  const groups = useMemo<Group[]>(() => {
    if (!viewer) return [];
    if (isOwner) {
      const out: Group[] = [{
        key: 'general', threadId: null, title: t('comments.title'), kind: 'general',
        hint: t('share.comments.generalHint'), readOnly: false,
        comments: comments.filter(c => (c.threadId ?? null) === null),
      }];
      for (const th of threads ?? []) {
        out.push({
          key: `t${th.id}`, threadId: th.id, kind: th.kind,
          title: t('share.comments.threadWith', { name: th.label }),
          readOnly: th.status !== 'active', promoted: th.promoted,
          comments: comments.filter(c => c.threadId === th.id),
        });
      }
      return out;
    }
    if (isRecipient && viewer.share) {
      const own = viewer.share.id;
      const out: Group[] = [{
        key: `t${own}`, threadId: own, kind: viewer.share.kind,
        title: t('share.comments.privateWith', { name: t('share.comments.theSender') }),
        hint: t('share.comments.privateHint'), readOnly: false,
        comments: comments.filter(c => c.threadId === own),
      }];
      const others = Array.from(new Set(comments.map(c => c.threadId).filter((id): id is number => typeof id === 'number' && id !== own)));
      const writable = viewer.writableThreads === 'any' ? [] : viewer.writableThreads;
      for (const id of writable) if (!others.includes(id) && id !== own) others.push(id);
      for (const id of others) {
        const label = viewer.threadLabels?.[String(id)];
        out.push({
          key: `t${id}`, threadId: id, kind: (label?.kind as 'retailer' | 'installer') || 'retailer',
          title: t('share.comments.onBehalfOf', { name: label?.label ?? t('share.comments.retailerThread') }),
          hint: t('share.comments.actingFor'), readOnly: false,
          comments: comments.filter(c => c.threadId === id),
        });
      }
      return out;
    }
    return [];
  }, [viewer, isOwner, isRecipient, threads, comments, t]);

  const handleAddComment = (threadId: number | null | undefined) => {
    setReplyTo(null);
    setModalThread(threadId);
    setModalOpen(true);
  };

  const handleReply = (parentId: number) => {
    const parent = comments.find(c => c.id === parentId);
    setReplyTo({ parentId, author: parent?.authorName || t('comments.thisComment') });
    setModalThread(parent?.threadId ?? null);
    setModalOpen(true);
  };

  const handleCommentPosted = () => {
    loadComments(); // Refresh the list
    onActivity?.();
  };

  // Recipients speak as their location; the name is fixed so threads stay attributable.
  const fixedName = isRecipient && viewer?.share ? viewer.share.locationName : undefined;
  // Match the server: a session / membership / admin actor proves intent with the
  // CSRF token, a guest holding a link with Turnstile.
  const postAuthed = !!viewer && (viewer.via === 'session' || viewer.via === 'membership' || viewer.via === 'admin');

  const KindIcon: React.FC<{ kind: Group['kind'] }> = ({ kind }) => (
    kind === 'general' ? <Globe size={14} /> : kind === 'retailer' ? <Store size={14} /> : <HardHat size={14} />
  );

  return (
    <div ref={sectionRef} id="estimate-comments" className="comments-section">
      {/* Header */}
      <div className="comments-header">
        <div className="comments-header-left">
          <h2 className="comments-title">
            <MessageSquare size={22} className="comments-title-icon" />
            {grouped && isRecipient ? t('share.comments.sectionTitleRecipient') : grouped ? t('share.comments.sectionTitleOwner') : t('comments.title')}
            {comments.length > 0 && (
              <span className="comments-count">{comments.length}</span>
            )}
          </h2>
        </div>
        {!grouped && (
          <button
            type="button"
            className="comments-add-btn"
            onClick={() => handleAddComment(isRecipient ? undefined : null)}
          >
            <Plus size={16} />
            {t('comments.add')}
          </button>
        )}
      </div>

      {/* Comment list */}
      <div className="comments-body">
        {loading ? (
          <div className="comments-loading">
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            <span>{t('comments.loading')}</span>
          </div>
        ) : error ? (
          <div className="comments-error">
            <p>{error}</p>
            <button
              type="button"
              className="comments-retry-btn"
              onClick={() => { setLoading(true); loadComments(); }}
            >
              {t('comments.retry')}
            </button>
          </div>
        ) : grouped ? (
          <div className="comments-groups">
            {groups.map(g => (
              <section key={g.key} id={g.threadId !== null ? `estimate-comments-thread-${g.threadId}` : 'estimate-comments-general'} className={`comments-group comments-group--${g.kind} ${g.readOnly ? 'comments-group--closed' : ''}`}>
                <div className="comments-group__head">
                  <h3 className="comments-group__title">
                    <KindIcon kind={g.kind} /> {g.title}
                    {g.comments.length > 0 && <span className="comments-count">{g.comments.length}</span>}
                    {g.promoted && <span className="comments-group__badge">{t('share.comments.promotedBadge')}</span>}
                    {g.readOnly && <span className="comments-group__badge comments-group__badge--closed"><Lock size={11} /> {t('share.comments.closed')}</span>}
                  </h3>
                  {!g.readOnly && (
                    <button type="button" className="comments-add-btn comments-add-btn--sm" onClick={() => handleAddComment(isRecipient && g.threadId === viewer?.share?.id ? undefined : g.threadId)}>
                      {g.comments.length > 0 ? <Reply size={14} /> : <Plus size={14} />}
                      {g.comments.length > 0 ? t('comments.reply') : t('comments.add')}
                    </button>
                  )}
                </div>
                {g.hint && <p className="comments-group__hint">{g.hint}</p>}
                <CommentThread comments={g.comments} onReply={g.readOnly ? undefined : handleReply} emptyText={t('share.comments.emptyThread')} />
              </section>
            ))}
          </div>
        ) : (
          <CommentThread comments={comments} onReply={handleReply} />
        )}
      </div>

      {/* Modal */}
      <CommentModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setReplyTo(null); }}
        estimateGuid={estimateGuid}
        parentId={replyTo?.parentId ?? null}
        parentAuthor={replyTo?.author}
        threadId={modalThread}
        fixedName={fixedName}
        authed={postAuthed}
        onSuccess={handleCommentPosted}
      />
    </div>
  );
};
